import { Badge } from "../../components/core/basic/ButtonGroup";
import SummaryCard from "../../components/core/blocks/SummaryCard";
import type React from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDates } from "../../dates/DatesContext";
import { useDebounce } from "@reactuses/core";
import useQueryParams from "../../hooks/useQueryParams";
import { useFormatTargetText } from "../../utils/useFormatTargetText";
import useSearchTermReport from "../../hooks/useSearchTermReport";
import { css } from "../../../styled-system/css";
import { Box, Flex, HStack, VStack } from "../../../styled-system/jsx";
import { apiClient } from "../../zodios/client";
import { useQuery } from "@tanstack/react-query";
import { QUERY_STAGE_COLOR_MAP } from "../../components/core/blocks/AdPlan/constants";
import FunnelChart from "./FunnelChart";
import { ButtonGroup } from "../../components/core/basic/ButtonGroup";
import { Accordion } from "../../components/core/basic/Accordion";
import QueryStatsCards, { STAGE_LABEL_SELECTORS } from "./QueryStatsCards";
import { type TSelector } from "../../i18n/types";
import { createAppColumnHelper, useClientTable } from "../../components/table/tableFeatures";
import { Table } from "../../components/table/Table";
import { RT8Pagination } from "../../components/table/Pagination";
import { SearchInput } from "../../components/fields/base/TextField";
import { getTrendColor } from "../../components/custom/summaryReport/utils";
import { formatPercent } from "../../utils/formatNumber";
import Loading from "../../components/core/blocks/Loading";
import QueryExpander from "./QueryExpander";
import type { FormatType, QueryFunnelProps, QueryData, StageAggStats, Stage, ColumnSpec, Deltas } from "./types";

const QueryFunnel: React.FC<QueryFunnelProps> = ({ category: category_id }) => {
  const { t } = useTranslation(["query_funnel", "common"]);
  const formatTargetText = useFormatTargetText();
  const CHART_METRIC_OPTIONS: {
    value: string;
    label: string;
    format: FormatType;
  }[] = useMemo(
    () => [
      {
        value: "ad_sales",
        label: t(($) => $.query_funnel.chartMetrics["Ad Sales"]),
        format: "currency",
      },
      {
        value: "ad_spend",
        label: t(($) => $.query_funnel.chartMetrics["Ad Spend"]),
        format: "currency",
      },
      {
        value: "count",
        label: t(($) => $.query_funnel.chartMetrics["# of Queries"]),
        format: "number",
      },
      {
        value: "clicks",
        label: t(($) => $.query_funnel.chartMetrics.Clicks),
        format: "number",
      },
      {
        value: "impressions",
        label: t(($) => $.query_funnel.chartMetrics.Impressions),
        format: "number",
      },
    ],
    [t],
  );
  const { start, end, preStart, preEnd, group } = useDates();
  const startDate = start.format("YYYY-MM-DD");
  const endDate = end.format("YYYY-MM-DD");
  const dims = "query";
  const [selectedStage, setSelectedStage] = useState<{ label: string; value: string } | null>({
    label: "All Queries",
    value: "all",
  });
  const [_query] = useQueryParams("q", "");
  const [funnelchartMetricValue, setfunnelmetricValue] = useState("ad_sales");
  const funnelchartMetric = useMemo(
    () => CHART_METRIC_OPTIONS.find((opt) => opt.value === funnelchartMetricValue) ?? CHART_METRIC_OPTIONS[0],
    [CHART_METRIC_OPTIONS, funnelchartMetricValue],
  );

  const query = useDebounce(_query, 300);
  const { data: allqueries } = useQuery({
    queryKey: ["queries", category_id, startDate, endDate, group],
    queryFn: async () =>
      await apiClient.getquerystages({
        params: { category_id: category_id },
        queries: { start: startDate, end: endDate, group },
      }),
  });
  const { data: current, isLoading: isCurrentLoading } = useSearchTermReport(
    query,
    start.format("YYYY-MM-DD"),
    end.format("YYYY-MM-DD"),
    500,
    dims,
    { category: category_id, sort: "-ad_spend" },
  );
  const { data: previous, isLoading: isPreviousLoading } = useSearchTermReport(
    query,
    preStart.format("YYYY-MM-DD"),
    preEnd.format("YYYY-MM-DD"),
    500,
    dims,
    { category: category_id, sort: "-ad_spend" },
  );
  const data = useMemo(() => {
    if (current && allqueries) {
      const select: (row: QueryData) => string = (d) => d.query;
      const previousMap: Record<string, QueryData> =
        previous?.reduce((acc: Record<string, QueryData>, row: QueryData) => {
          acc[select(row)] = row;
          return acc;
        }, {}) ?? {};
      const stageMap: Record<string, string> = {};
      allqueries.stages.forEach(({ stage_name, queries }) => {
        if (stage_name) {
          queries.forEach((q) => {
            if (q) {
              stageMap[q.toLowerCase()] = stage_name;
            }
          });
        }
      });

      return current.map((row: QueryData) => {
        const key = select(row);
        row["key"] = key;
        const prevRow = previousMap[key];
        const compareFn = (metric: string, row: QueryData, prevRow: QueryData) => {
          const currValue = row[metric] as number;
          const prevValue = prevRow[metric] as number;
          return {
            [`${metric}_delta`]: currValue - prevValue,
            [`${metric}_percent`]: prevValue !== 0 ? (currValue - prevValue) / prevValue : null,
            [`${metric}_prev`]: prevValue,
          };
        };
        const stage = stageMap[key.toLowerCase()] || "unknown";
        if (stage === "unknown" && key.toLowerCase().startsWith("b0")) {
          row["stage"] = "consideration";
        } else {
          row["stage"] = stage;
        }
        if (prevRow) {
          return {
            ...row,
            ...compareFn("impressions", row, prevRow),
            ...compareFn("clicks", row, prevRow),
            ...compareFn("ad_spend", row, prevRow),
            ...compareFn("cost", row, prevRow),
            ...compareFn("ad_sales", row, prevRow),
            ...compareFn("acos", row, prevRow),
            ...compareFn("roas", row, prevRow),
            ...compareFn("click_thru_rate", row, prevRow),
            ...compareFn("cost_per_click", row, prevRow),
            ...compareFn("conv_rate", row, prevRow),
            ...compareFn("conversion", row, prevRow),
            ...compareFn("viewable_impressions", row, prevRow),
            ...compareFn("viewability_rate", row, prevRow),
            ...compareFn("view_click_through_rate", row, prevRow),
          };
        }
        return row;
      });
    }
    return [];
  }, [current, previous, allqueries]);

  const AggStats = useMemo<Record<string, StageAggStats> | null>(() => {
    if (!data || data.length === 0) return null;
    const stageMap: Record<string, string> = {};
    if (allqueries?.stages) {
      allqueries.stages.forEach(({ stage_name, queries }) => {
        if (stage_name) {
          queries.forEach((q) => {
            if (q) {
              stageMap[q.toLowerCase()] = stage_name;
            }
          });
        }
      });
    }
    const aggStats: Record<string, StageAggStats> = data.reduce(
      (acc: Record<string, StageAggStats>, row: QueryData) => {
        const stage = row.stage || "Unknown";
        if (!acc[stage]) {
          acc[stage] = {
            impressions: 0,
            clicks: 0,
            ad_spend: 0,
            ad_sales: 0,
            count: 0,
            ad_sales_delta: 0,
            ad_spend_delta: 0,
            clicks_delta: 0,
            impressions_delta: 0,
            impressions_percent: 0,
            clicks_percent: 0,
            ad_spend_percent: 0,
            ad_sales_percent: 0,
            ad_spend_prev: 0,
            ad_sales_prev: 0,
            clicks_prev: 0,
            impressions_prev: 0,
          };
        }
        const agg = acc[stage];
        agg.impressions += row.impressions || 0;
        agg.clicks += row.clicks || 0;
        agg.ad_spend += row.ad_spend || 0;
        agg.ad_sales += row.ad_sales || 0;
        agg.ad_sales_delta += (row.ad_sales || 0) - ((row.ad_sales_prev as number) || 0);
        agg.ad_spend_delta += (row.ad_spend || 0) - ((row.ad_spend_prev as number) || 0);
        agg.clicks_delta += (row.clicks || 0) - ((row.clicks_prev as number) || 0);
        agg.impressions_delta += (row.impressions || 0) - ((row.impressions_prev as number) || 0);
        agg.ad_spend_prev += (row.ad_spend_prev as number) || 0;
        agg.ad_sales_prev += (row.ad_sales_prev as number) || 0;
        agg.clicks_prev += (row.clicks_prev as number) || 0;
        agg.impressions_prev += (row.impressions_prev as number) || 0;
        agg.count += 1;
        return acc;
      },
      {},
    );

    Object.keys(aggStats).forEach((stage) => {
      const stats = aggStats[stage];
      if (stats) {
        stats.ctr = stats.impressions ? stats.clicks / stats.impressions : 0;
        stats.acos = stats.ad_sales ? stats.ad_spend / stats.ad_sales : 0;
        stats.ad_sales_percent = stats.ad_sales_delta / stats.ad_sales || 0;
        stats.ad_spend_percent = stats.ad_spend_delta / stats.ad_spend || 0;
        stats.clicks_percent = stats.clicks_delta / stats.clicks || 0;
        stats.impressions_percent = stats.impressions_delta / stats.impressions || 0;
        stats.acos_delta = stats.acos - (stats.ad_sales_prev ? stats.ad_spend_prev / stats.ad_sales_prev : 0);
        stats.click_thru_rate_delta =
          stats.ctr - (stats.impressions_prev ? stats.clicks_prev / stats.impressions_prev : 0);
      }
    });

    const orderedAggStats: Record<string, StageAggStats> = {};
    const STAGE_ORDER = ["awareness", "consideration", "conversion", "loyalty", "unknown"];
    STAGE_ORDER.forEach((stage) => {
      if (aggStats[stage]) {
        orderedAggStats[stage] = aggStats[stage];
      }
    });

    return orderedAggStats;
  }, [data, allqueries]);
  const funnelChartData = useMemo(() => {
    if (!AggStats) return [];
    return Object.entries(AggStats)
      .map(([stage, stats]) => {
        const stageSelector = (STAGE_LABEL_SELECTORS as Record<string, TSelector | undefined>)[stage];
        return {
          name: stageSelector ? t(stageSelector) : stage,
          y: stats[funnelchartMetric?.value as keyof StageAggStats] ?? 0,
          color: QUERY_STAGE_COLOR_MAP[stage as Stage] || "#ccc",
        };
      })
      .filter((entry) => entry.y > 0);
  }, [AggStats, funnelchartMetric, t]);

  const columnSpecs = useMemo<ColumnSpec[]>(() => {
    const cols: ColumnSpec[] = [
      {
        key: "impressions",
        compareKey: "impressions_percent",
        options: { columnTitle: ($) => $.query_funnel.columns.Impressions, format: "number" },
      },
      {
        key: "clicks",
        compareKey: "clicks_percent",
        options: { columnTitle: ($) => $.query_funnel.columns.Clicks, format: "number" },
      },
      {
        key: "ad_spend",
        compareKey: "ad_spend_percent",
        options: { columnTitle: ($) => $.query_funnel.columns["Ad Spend"], format: "currency" },
      },
      {
        key: "ad_sales",
        compareKey: "ad_sales_percent",
        options: { columnTitle: ($) => $.query_funnel.columns["Ad Sales"], format: "currency" },
      },
      {
        key: "acos",
        compareKey: "acos_delta",
        options: { columnTitle: ($) => $.query_funnel.columns.Acos, format: "percentage" },
      },
      {
        key: "cost_per_click",
        compareKey: "cost_per_click_percent",
        options: { columnTitle: ($) => $.query_funnel.columns.CPC, format: "currency" },
      },
      {
        key: "conv_rate",
        compareKey: "conv_rate_delta",
        options: { columnTitle: ($) => $.query_funnel.columns.ConversionRate, format: "percentage" },
      },
      {
        key: "click_thru_rate",
        compareKey: "click_thru_rate_delta",
        options: { columnTitle: ($) => $.query_funnel.columns.CTR, format: "percentage" },
      },
      {
        key: "viewable_impressions",
        compareKey: "viewable_impressions_percent",
        options: { columnTitle: ($) => $.query_funnel.columns["Viewable Impressions"], format: "number" },
      },
      {
        key: "viewability_rate",
        compareKey: "viewability_rate_delta",
        options: { columnTitle: ($) => $.query_funnel.columns["Viewability Rate"], format: "percentage" },
      },
      {
        key: "view_click_through_rate",
        compareKey: "view_click_through_rate_delta",
        options: { columnTitle: ($) => $.query_funnel.columns["View Click through Rate"], format: "percentage" },
      },
      {
        key: "adv_keyword_cnt",
        options: {
          columnTitle: ($) => $.query_funnel.columns["Keyword Count"],
          format: "number",
          formatter: (v) => v,
        },
      },
      {
        key: "adv_target_cnt",
        options: {
          columnTitle: ($) => $.query_funnel.columns["Target Count"],
          format: "number",
          formatter: (v) => v,
        },
      },
    ];
    cols.unshift({
      key: "stage",
      options: {
        columnTitle: ($) => $.query_funnel.columns.Stage,
        format: "text",
        formatter: (v) => v,
        hideFilter: true,
      },
    });
    return cols;
  }, []);

  const filteredData = useMemo(() => {
    if (selectedStage?.value === "all") return data;
    return data.filter((row: QueryData) => row.stage === selectedStage?.value);
  }, [data, selectedStage]);
  const columnHelper = createAppColumnHelper<QueryData>();

  const columns = useMemo(() => {
    return columnSpecs.map((col) => {
      return columnHelper.accessor(col.key, {
        id: col.key,
        header: t(col.options.columnTitle),
        cell: ({ getValue, row }) => {
          const value = getValue();
          const compareKey = col.compareKey as keyof Deltas | undefined;
          const compareValue = compareKey ? row.original[compareKey] : undefined;
          const format = col.options.format;
          const formatter =
            col.options.formatter ||
            ((v) => {
              switch (format) {
                case "number":
                  return v;
                case "currency":
                  if (v >= 1000) return `$${(v / 1000).toFixed(2)}k`;
                  return `$${v.toFixed(2)}`;
                case "percentage":
                  return `${(v * 100).toFixed(2)}%`;
                default:
                  return String(v);
              }
            });

          const formattedValue = formatter(value as number, row.original);
          const prevValueStyle = {
            color: getTrendColor(compareValue, col.key, "rgb(15, 195, 152)", "rgb(240, 60, 103)"),
            fontWeight: "bold",
          };
          return (
            <VStack>
              <span>{formattedValue}</span>
              {compareValue !== undefined && (
                <span style={prevValueStyle}>
                  {formatPercent(compareValue, {
                    decimalPlaces: 1,
                    addPlus: true,
                  })}
                </span>
              )}
            </VStack>
          );
        },
      });
    });
  }, [columnSpecs, columnHelper, t]);

  columns.unshift(
    columnHelper.accessor("key", {
      id: "key",
      header: t(($) => $.query_funnel.columns.Query),
      cell: ({ getValue, row }) => {
        const value = getValue();
        const stage = row.original.stage;
        const stageSelector = stage ? STAGE_LABEL_SELECTORS[stage as Stage] : null;
        const stageLabel = stageSelector ? t(stageSelector) : stage;
        return (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "0.4rem" }}>
            {formatTargetText(value)}
            {stage && <Badge className="bg-trellis-fuchsia me-3">{stageLabel}</Badge>}
          </div>
        );
      },
      size: 300,
    }),
  );
  const table = useClientTable({
    data: filteredData,
    columns,
    getRowCanExpand: () => true,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 50 },
    },
  });
  if (isCurrentLoading || isPreviousLoading) {
    return <Loading />;
  }
  return (
    <>
      <Accordion title={t(($) => $.query_funnel.accordions["Query Stats"])}>
        <SummaryCard title={t(($) => $.query_funnel.cards["Query funnel Report"])}>
          <HStack className={css({ minHeight: "calc(100vh - 350px)" })}>
            <Flex gap={"15rem"} justifyContent={"space-between"}>
              <div className={css({ marginTop: "1rem" })}>
                <FunnelChart data={funnelChartData} format={funnelchartMetric?.format ?? "number"} />
                <Box
                  className={css({
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    marginTop: "5",
                    paddingTop: "5",
                  })}
                >
                  <ButtonGroup
                    buttons={CHART_METRIC_OPTIONS}
                    value={funnelchartMetric?.value ?? ""}
                    onSelect={(val) => {
                      setfunnelmetricValue(val);
                    }}
                  />
                </Box>
              </div>
              <div>
                <QueryStatsCards
                  AggStats={AggStats}
                  selectedStage={selectedStage}
                  setSelectedStage={setSelectedStage}
                />
              </div>
            </Flex>
          </HStack>
        </SummaryCard>
      </Accordion>
      <Accordion title={t(($) => $.query_funnel.accordions["Query Funnel Report"])}>
        <SummaryCard title={t(($) => $.query_funnel.cards["Query funnel Report"])}>
          <HStack className={css({ minHeight: "calc(100vh - 350px)" })}>
            <div className={css({ display: "flex", flexDirection: "column", width: "100%" })}>
              <div
                className={css({
                  display: "flex",
                  justifyContent: "flex-start",
                })}
              >
                <div className={css({ width: "400px" })}>
                  <SearchInput
                    aria-label={t(($) => $.common.searchBar["Search..."])}
                    value={table.state.globalFilter ?? ""}
                    placeholder={t(($) => $.common.searchBar["Search..."])}
                    onChange={(val) => {
                      table.setGlobalFilter(val);
                    }}
                    css={{ minWidth: "250px" }}
                  />
                </div>
              </div>
              <div className={css({ w: "full", padding: "2rem" })}>
                <Table
                  table={table}
                  renderExpander={(row) => (
                    <QueryExpander
                      row={row}
                      dims={dims}
                      query={row.original.query}
                      keyword_text={row.original.keyword_text}
                      match_type={row.original.match_type}
                      context="query-funnel"
                    />
                  )}
                />
                <Flex className={css({ marginTop: "1rem" })} justifyContent={"flex-end"}>
                  <RT8Pagination table={table} />
                </Flex>
              </div>
            </div>
          </HStack>
        </SummaryCard>
      </Accordion>
    </>
  );
};

export default QueryFunnel;
